'use client';

import type {
  GetLikedProductsServiceResponse,
  LikedProductService,
} from '@musinsa/services-curator';
import type { InfiniteData } from '@tanstack/react-query';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { curatorQueries } from '../../query-factory';

interface Props {
  size?: number;
}

export const useLikedProductsInfiniteQuery = ({ size = 20 }: Props) => {
  const queryClient = useQueryClient();
  const likedQueryOptions = useMemo(() => curatorQueries.products.liked({ size }), [size]);

  const query = useInfiniteQuery({
    ...likedQueryOptions,
    initialPageParam: 1,
    getNextPageParam: (lastResponse: GetLikedProductsServiceResponse) =>
      lastResponse.pagination.hasNextPage ? lastResponse.pagination.nextPage : undefined,
  });

  const flattenedData: LikedProductService[] = useMemo(
    () => query.data?.pages.flatMap((page) => page.list) ?? [],
    [query.data],
  );

  const total = useMemo(() => query.data?.pages[0]?.total ?? 0, [query.data]);

  const removeProductFromCache = useCallback(
    (goodsNo: string | number) => {
      const targetGoodsNo = goodsNo.toString();

      queryClient.setQueryData<InfiniteData<GetLikedProductsServiceResponse>>(
        likedQueryOptions.queryKey,
        (prev) => {
          if (!prev) return prev;

          let removed = false;
          const nextPages = prev.pages.map((page) => {
            const filteredList = page.list.filter(
              (item) => item.goodsNo.toString() !== targetGoodsNo,
            );

            if (filteredList.length !== page.list.length) {
              removed = true;
              return {
                ...page,
                list: filteredList,
              };
            }

            return page;
          });

          if (!removed) return prev;

          return {
            ...prev,
            pages: nextPages,
          };
        },
      );
    },
    [likedQueryOptions.queryKey, queryClient],
  );

  return {
    ...query,
    products: flattenedData,
    total,
    removeProductFromCache,
  };
};
